import BaseSetup from '@/components/BaseSetup';
import type OrganizationUserEntity from '@/entity/organization-user/OrganizationUserEntity';
import type OrganizationUserListItemEntity from '@/entity/organization-user/OrganizationUserListItemEntity';
import OrganizationRoleListItemEntity from '@/entity/OrganizationRoleListItemEntity';
import SelfEntity from '@/entity/SelfEntity';
import { i18n } from '@/i18n';
import useColorService from '@/services/ColorService';
import useOrganizationRoleService from '@/services/organization/OrganizationRoleService';
import useRoleUserService from '@/services/RoleUserService';
import { Reactive } from '@/utils/decorators';
import { useNotif } from '@orion.ui/orion';
import { ref } from 'vue';

export type SharedRoleFlatSelectEmits = {
	(e: 'update:opened', val: boolean): void
	(e: 'update:modelValue', val: IRoleUserModel[]): void
	(e: 'add' | 'delete', val: OrganizationRoleListItemEntity): void
};

export type SharedRoleFlatSelectProps = {
	hideNew?: boolean
	opened?: boolean
	disabled?: boolean
	modelValue?: IRoleUserModel[]
	user?: OrganizationUserListItemEntity | OrganizationUserEntity | SelfEntity
	placement?: string
	label?: string
};

export default class SharedRoleFlatSelectSetup extends BaseSetup {

	static readonly defaultProps = {
		modelValue: () => [],
		placement: 'bottom-start',
		label: i18n.global.t('common.form.selectRole'),
	};

	_popover = ref<Dropdown>();

	@Reactive private readonly state = {
		popoverOpened: false,
		loading: false,
		search: undefined as Undef<string>,
		roles: [] as OrganizationRoleListItemEntity[],
	};

	get loading () { return this.state.loading }
	get roles () { return this.state.roles as OrganizationRoleListItemEntity[] }
	get popoverOpened () { return this.state.popoverOpened }
	get rolesList () {
		return this.roles
			.filter(x => !this.search?.trim().length || x.name?.toLowerCase().includes(this.search))
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
	}

	get rolesListOptionsForFlatSelect (): FlatSelectOptionItem[] {
		return this.rolesList.map(x => ({
			id: x.id,
			name: x.name ?? '',
			colorName: x.colorName,
		}));
	}

	get search () { return this.state.search }
	set search (val) { this.state.search = val }

	constructor (
		private props: SharedRoleFlatSelectProps,
		private emits: SharedRoleFlatSelectEmits,
	) {
		super();
	}

	swapRoleColor (color?: string) {
		return (useColorService().swapColor(color) ?? 'default');
	}

	roleIsSelected (role: FlatSelectOptionItem) {
		return !!this.props.modelValue?.map((x: IRoleUserModel) => x?.organizationRole?.id).includes(role.id);
	}

	async handlePopoverShowAsync () {
		this.emits('update:opened', true);
		this.state.popoverOpened = true;
		this.state.loading = true;
		this.state.roles = OrganizationRoleListItemEntity.map(await useOrganizationRoleService().listAsync());
		this.state.loading = false;
	}

	handlePopoverHide () {
		this.emits('update:opened', false);
		this.state.popoverOpened = false;
	}

	async linkAsync (role: IOrganizationRoleListItemModel) {
		if (!this.props.user?.userId) return;

		const newRole = await useRoleUserService().addAsync(this.props.user.userId, role.id);

		// if (newRole) this.props.user.addUserRole(newRole);

		this.emits('update:modelValue', [...(this.props.modelValue ?? []), newRole as IRoleUserModel]);
		const roleEntity = this.roles.findByKey(role.id);
		if (roleEntity) this.emits('add', roleEntity);

		useNotif.success(this.$t('common.label.saving'), this.$t('auth.role.addSuccess', {
			roleName: role.name,
			userName: this.props.user.name,
		}));
	}

	async unlinkAsync (role: IOrganizationRoleListItemModel) {
		if (!this.props.user?.userId) return;

		const userRoleIndex = this.props.modelValue?.findIndex((r: IRoleUserModel) => r.organizationRole?.id === role.id);
		if (userRoleIndex === -1 || userRoleIndex === undefined) return;

		const roleUserId = this.props.modelValue?.[userRoleIndex]?.id;

		if (roleUserId) {
			await useRoleUserService().deleteAsync(roleUserId, this.props.user.userId);
			this.props.user.deleteUserRole(roleUserId);
		}

		this.emits('update:modelValue', this.props.modelValue?.filter((x: IRoleUserModel) => x.organizationRole?.id !== role.id) ?? []);

		const roleEntity = this.roles.findByKey(role.id);
		if (roleEntity) this.emits('delete', roleEntity);

		useNotif.success(this.$t('auth.role.removeSuccess', { roleName: role.name, userName: this.props.user.name }));
	}

	private show () {
		this._popover.value?.show();
	}

	private hide () {
		this._popover.value?.hide();
	}

	async createRoleAsync () {
		this.hide();
		try {
			await useOrganizationRoleService().promptNewRoleAsideAsync();
			this.state.roles = OrganizationRoleListItemEntity.map(await useOrganizationRoleService().listAsync(true));
		}
		finally {
			setTimeout(() => {
				this.show();
			}, 600);
		}
	}

}
